import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Grid, 
  Alert, 
  CircularProgress, 
  Divider,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  MenuItem
} from '@mui/material';
import { 
  Upload, 
  FileText, 
  Trash2, 
  AlertCircle, 
  CheckCircle,
  Plus
} from 'lucide-react';
import { apiService } from '../services/api';
import { format } from 'date-fns';

const MaintenanceInvoiceSection = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    service_provider: '',
    work_performed: '',
    file: null
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [vehicleConflict, setVehicleConflict] = useState(null);
  const [userVehicle, setUserVehicle] = useState(null);

  useEffect(() => {
    fetchInvoices();
    fetchUserInfo();
    fetchVehicleConflictStatus();
  }, []);

  const fetchVehicleConflictStatus = async () => {
    try {
      const response = await apiService.getVehicleConflictStatus();
      if (response?.hasConflict) {
        setVehicleConflict(response);
      }
    } catch (err) {
      console.error('Error fetching vehicle conflict status:', err);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getUserMaintenanceInvoices();
      setInvoices(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching maintenance invoices:', err);
      setError('Failed to load maintenance invoices. Please try again later.');
      setLoading(false);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const userData = await apiService.getUser();
      if (userData.vehicle_number) {
        setUserVehicle(userData.vehicle_number);
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      // Don't show error to user
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setInvoiceData(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!invoiceData.service_provider || !invoiceData.work_performed || !invoiceData.file) {
      setError('Please fill in all required fields and select a file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      
      await apiService.uploadMaintenanceInvoice(invoiceData);
      
      setInvoiceData({
        service_provider: '',
        work_performed: '',
        file: null
      });
      
      setSuccess('Maintenance invoice uploaded successfully');
      setUploadDialogOpen(false);
      
      // Refresh invoices list
      await fetchInvoices();
      
      setUploading(false);
    } catch (err) {
      console.error('Error uploading invoice:', err);
      setError(err.toString());
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;

    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteMaintenanceInvoice(selectedInvoice.id);
      
      setSuccess('Maintenance invoice deleted successfully');
      setDeleteConfirmOpen(false);
      setSelectedInvoice(null);
      
      // Refresh invoices list
      await fetchInvoices();
      
      setLoading(false);
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError('Failed to delete invoice. Please try again later.');
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    if (invoice.file_url) {
      window.open(invoice.file_url, '_blank');
    } else {
      setError('Unable to access invoice file');
    }
  };

  // If user has vehicle conflict, show blocked message
  if (vehicleConflict?.hasConflict) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            <FileText size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Maintenance Invoices
          </Typography>
          <Alert severity="warning">
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              Feature Temporarily Blocked
            </Typography>
            <Typography variant="body2">
              Maintenance invoice uploads are blocked because your vehicle unit number ({vehicleConflict.vehicleNumber}) is currently also assigned to {vehicleConflict.conflictUsername}.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Please speak to your service manager to resolve this conflict.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <FileText size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            Maintenance Invoices
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Plus size={16} />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Invoice
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : invoices.length > 0 ? (
          <List sx={{ width: '100%' }}>
            {invoices.map((invoice) => (
              <Paper key={invoice.id} variant="outlined" sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {invoice.service_provider} - {invoice.work_performed}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Uploaded: {format(new Date(invoice.created_at), 'MM/dd/yyyy')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          File: {invoice.original_filename}
                        </Typography>
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Invoice">
                        <IconButton 
                          edge="end" 
                          aria-label="view" 
                          onClick={() => handleViewInvoice(invoice)}
                          color="primary"
                        >
                          <FileText size={18} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Invoice">
                        <IconButton 
                          edge="end" 
                          aria-label="delete" 
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setDeleteConfirmOpen(true);
                          }}
                          color="error"
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              </Paper>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <AlertCircle size={40} color="#888" style={{ marginBottom: '8px' }} />
            <Typography variant="body1" color="text.secondary">
              No maintenance invoices uploaded yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Upload invoices for oil changes, tire rotations, and other maintenance work.
            </Typography>
          </Box>
        )}

        {/* Upload Dialog */}
        <Dialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Upload Maintenance Invoice</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Provider"
                  placeholder="e.g., Jiffy Lube, Firestone, GM"
                  value={invoiceData.service_provider}
                  onChange={(e) => setInvoiceData({ ...invoiceData, service_provider: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Work Performed"
                  placeholder="e.g., Oil Change, Tire Rotation, Engine Service"
                  value={invoiceData.work_performed}
                  onChange={(e) => setInvoiceData({ ...invoiceData, work_performed: e.target.value })}
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              {userVehicle && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Your Vehicle"
                    value={userVehicle}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="This is your assigned vehicle"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <input
                  accept="image/jpeg,image/png,application/pdf"
                  style={{ display: 'none' }}
                  id="invoice-file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="invoice-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<Upload size={16} />}
                  >
                    {invoiceData.file ? invoiceData.file.name : 'Select Invoice File'}
                  </Button>
                </label>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Accepted formats: JPG, PNG, PDF (max 10MB)
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setUploadDialogOpen(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={
                uploading || 
                !invoiceData.service_provider || 
                !invoiceData.work_performed || 
                !invoiceData.file
              }
              startIcon={uploading ? <CircularProgress size={20} /> : <CheckCircle size={20} />}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this maintenance invoice?
            </Typography>
            {selectedInvoice && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Service Provider:</strong> {selectedInvoice.service_provider}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Work Performed:</strong> {selectedInvoice.work_performed}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MaintenanceInvoiceSection;